import { notification } from 'antd'
import dayjs from 'dayjs'

interface DownloadItem {
    url: string
    fileName?: string
}

function splitFileName(fileName = 'download'): { baseName: string; extension: string } {
    const lastDotIndex = fileName.lastIndexOf('.')
    if (lastDotIndex <= 0) return { baseName: fileName, extension: '' }

    return {
        baseName: fileName.slice(0, lastDotIndex),
        extension: fileName.slice(lastDotIndex),
    }
}

export function truncateMiddle(text: string, maxLen = 50): string {
    if (!text || text.length <= maxLen) return text

    const separator = '...'
    const charsToShow = maxLen - separator.length
    const frontChars = Math.ceil(charsToShow * 0.4)
    const backChars = Math.floor(charsToShow * 0.6)

    return text.slice(0, frontChars) + separator + text.slice(-backChars)
}

export type FileFormatMode = 'display' | 'download'

export function formatFileName(fileName?: string, mode: FileFormatMode = 'display'): string {
    const defaultName = fileName || 'download'
    const { baseName, extension } = splitFileName(defaultName)

    if (mode === 'display') {
        if (/\(\d{2}-\d{2}-\d{4} \d{2}:\d{2}\)$/.test(defaultName)) {
            return truncateMiddle(defaultName, 50)
        }
        const timestamp = dayjs().format('DD-MM-YYYY HH:mm')
        const fullName = `${baseName}${extension} (${timestamp})`

        return truncateMiddle(fullName, 50)
    }

    if (/_\d{4}-\d{2}-\d{2}_\d{6}$/.test(baseName)) {
        return defaultName
    }
    const timestamp = dayjs().format('YYYY-MM-DD_HHmmss')
    return `${baseName}_${timestamp}${extension}`
}

export async function downloadFileWithName(
    item: DownloadItem,
    opts?: {
        messageLoading?: string
        messageSuccess?: string
        showNotifications?: boolean
    },
): Promise<void> {
    const showNotifications = opts?.showNotifications ?? true
    const messageLoading = opts?.messageLoading ?? 'Đang tải file...'
    const messageSuccess = opts?.messageSuccess ?? 'Tải file thành công!'

    const toastKey = 'download-file'

    if (showNotifications) {
        notification.open({
            key: toastKey,
            title: messageLoading,
            type: 'info',
            duration: 0,
            placement: 'topRight',
        })
    }

    try {
        const res = await fetch(item.url)
        const blob = await res.blob()
        const downloadUrl = window.URL.createObjectURL(blob)

        const newFileName = formatFileName(item.fileName, 'download')
        
        const link = document.createElement('a')
        link.href = downloadUrl
        link.setAttribute('download', newFileName)
        document.body.appendChild(link)
        link.click()

        link.parentNode?.removeChild(link)
        window.URL.revokeObjectURL(downloadUrl)

        if (showNotifications) {
            notification.success({
                key: toastKey,
                title: messageSuccess,
                duration: 3,
                placement: 'topRight',
            })
        }
    } catch (error) {
        if (showNotifications) {
            notification.error({
                key: toastKey,
                title: 'Tải file thất bại',
                duration: 3,
                placement: 'topRight',
            })
        }
        throw error
    }
}

export async function downloadFiles(
    input: DownloadItem | DownloadItem[],
    opts?: {
        messageLoading?: string
        messageSuccess?: string
        showNotifications?: boolean
    },
): Promise<void> {
    const showNotifications = opts?.showNotifications ?? true
    const messageLoading = opts?.messageLoading ?? 'Đang tải file...'
    const messageSuccess = opts?.messageSuccess ?? 'Tải file thành công!'
    const toastKey = 'download-files'

    const files = Array.isArray(input) ? input : [input]

    if (showNotifications) {
        notification.info({
            key: toastKey,
            title: messageLoading,
            duration: 0,
        })
    }

    try {
        await Promise.all(files.map((file) => downloadFileWithName(file, opts)))

        if (showNotifications) {
            notification.success({
                key: toastKey,
                title: messageSuccess,
                duration: 3,
            })
        }
    } catch (error) {
        if (showNotifications) {
            notification.error({
                key: toastKey,
                title: 'Tải file thất bại',
                duration: 3,
            })
        }
        throw error
    }
}


